import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../../../../api/client';

// CX_INTERACTION_ID Phase D: "show the whole interaction as one unit" panel.
// Given a cx_interaction_id, renders the interaction summary, per-owner
// segments, every Asterisk leg, and the linked voice + screen recordings.

function fmtTime(s) {
  if (!s) return '—';
  return String(s).replace('T', ' ').replace(/\.\d+/, '').replace(/Z$/, '');
}

function fmtDur(sec) {
  if (sec == null) return '—';
  const s = Math.max(0, Math.round(Number(sec)));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

function Chip({ children, tone = 'muted' }) {
  const tones = {
    muted:   '#9CA3AF',
    caller:  '#5AB6FF',
    agent:   '#F5A623',
    consult: '#8B5CF6',
    conference: '#10B981',
    monitor: '#6B7280',
    transfer_target: '#EC4899',
    // segment kinds (S6)
    queue_wait:   '#64748B',
    agent_handle: '#F5A623',
    hold:         '#EAB308',
    parties_only: '#0EA5E9',
    parked:       '#6B7280',
    ivr:          '#A855F7',
    rona:         '#EF4444',
    third:        '#8B5CF6',
  };
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
      background: tones[children] || tones[tone] || tones.muted, color: '#fff',
    }}>
      {children}
    </span>
  );
}

export default function InteractionPanel({ interactionId, onClose }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['interaction', interactionId],
    queryFn: () => reportsApi.interaction(interactionId),
    enabled: !!interactionId,
    staleTime: 30_000,
  });

  const it = data?.interaction;

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-black/30 z-40" />
      <div className="fixed top-0 right-0 h-full w-[520px] max-w-[100vw] z-50 bg-surface-panel dark:bg-surface-dark-panel border-l border-surface-border dark:border-surface-dark-border shadow-2xl flex flex-col">
        <div className="px-4 py-3 border-b border-surface-border dark:border-surface-dark-border flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-xs text-surface-muted">Interaction</div>
            <div className="text-sm font-mono truncate flex items-center gap-2">
              {interactionId}
              <button
                type="button"
                title="Copy Interaction ID"
                onClick={() => { try { navigator.clipboard.writeText(interactionId); } catch (_) {} }}
                className="text-xs text-surface-muted hover:text-brand-500"
              >⧉</button>
            </div>
          </div>
          <button
            onClick={onClose}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle"
          >
            ✕ Close
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4 text-sm">
          {isLoading && <div className="text-surface-muted">Loading interaction…</div>}
          {isError && (
            <div className="text-red-600 dark:text-red-300">
              {error?.detail || error?.message || 'Failed to load interaction.'}
            </div>
          )}
          {data && it && (
            <>
              {/* Summary */}
              <div className="mb-4 p-3 rounded-thin bg-surface-subtle dark:bg-surface-dark-subtle grid grid-cols-2 gap-2 text-xs">
                <div><div className="text-surface-muted">Direction</div><div>{it.direction || '—'}</div></div>
                <div><div className="text-surface-muted">Origin</div><div>{it.origin || '—'}</div></div>
                <div><div className="text-surface-muted">Owner ext</div><div className="font-mono">{it.owner_ext || '—'}</div></div>
                <div><div className="text-surface-muted">Queue</div><div className="font-mono">{it.primary_queue_id || '—'}</div></div>
                <div><div className="text-surface-muted">External party</div><div className="font-mono">{it.external_party || '—'}</div></div>
                <div><div className="text-surface-muted">Disposition</div><div>{it.disposition || '—'}</div></div>
                <div><div className="text-surface-muted">Started</div><div className="font-mono">{fmtTime(it.started_at)}</div></div>
                <div><div className="text-surface-muted">Ended</div><div className="font-mono">{fmtTime(it.ended_at)}</div></div>
              </div>

              {/* Tiling quality (S6) */}
              {data.tiling && (
                <div className="mb-4 p-3 rounded-thin bg-surface-subtle dark:bg-surface-dark-subtle text-xs">
                  <div className="flex items-center justify-between mb-1">
                    <span className="uppercase tracking-wide text-surface-muted">Segment timeline</span>
                    <span style={{
                      padding: '2px 8px', borderRadius: 999, fontWeight: 600, color: '#fff',
                      background: data.tiling.tiled_ok ? '#10B981' : '#EF4444',
                    }}>{data.tiling.tiled_ok ? '✓ tiled' : `⚠ ${data.tiling.defects.length} defect(s)`}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div><div className="text-surface-muted">Interaction</div><div>{fmtDur(data.tiling.interaction_sec)}</div></div>
                    <div><div className="text-surface-muted">Segments Σ</div><div>{fmtDur(data.tiling.segments_sec)}</div></div>
                    <div><div className="text-surface-muted">Agent handle (AHT)</div><div>{fmtDur(data.tiling.agent_handle_sec)}</div></div>
                  </div>
                  {(data.tiling.defects || []).length > 0 && (
                    <div className="mt-2 text-red-600 dark:text-red-300">
                      {data.tiling.defects.map((d, i) => (
                        <div key={i}>• {d.type}{d.after_seq != null ? ` after #${d.after_seq}` : ''}: {d.seconds}s</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Segments (per-owner handling windows) */}
              <div className="mb-4">
                <div className="text-xs uppercase tracking-wide text-surface-muted mb-1">
                  Segments ({(data.segments || []).length})
                </div>
                <div className="space-y-1">
                  {(data.segments || []).map((s, i) => (
                    <div key={i} className="border border-surface-border dark:border-surface-dark-border rounded-thin p-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5">
                          <span className="text-surface-muted">#{s.seq}</span>
                          <Chip>{s.kind || 'agent_handle'}</Chip>
                          <span className="font-mono">{s.owner_ext || '—'}</span>
                          {s.is_agent_handle && <span title="counts toward AHT">⏱️</span>}
                        </span>
                        <span className="text-surface-muted">{s.is_agent_handle ? `talk ${fmtDur(s.talk_sec)}` : fmtDur(s.talk_sec)}</span>
                      </div>
                      <div className="text-surface-muted mt-1">
                        {fmtTime(s.started_at)} → {fmtTime(s.ended_at)} · <span className="italic">{s.reason || 'initial'}</span>
                      </div>
                    </div>
                  ))}
                  {(data.segments || []).length === 0 && <div className="text-surface-muted text-xs">No segments recorded.</div>}
                </div>
              </div>

              {/* Legs */}
              <div className="mb-4">
                <div className="text-xs uppercase tracking-wide text-surface-muted mb-1">
                  Legs ({(data.legs || []).length})
                </div>
                <div className="space-y-1">
                  {(data.legs || []).map((l, i) => (
                    <div key={i} className="border border-surface-border dark:border-surface-dark-border rounded-thin p-2 text-xs">
                      <div className="flex items-center justify-between">
                        <Chip>{l.leg_role || 'unknown'}</Chip>
                        <span className="text-surface-muted font-mono">{l.owner_ext || ''}</span>
                      </div>
                      <div className="font-mono mt-1 break-all">{l.channel_name || l.asterisk_uniqueid || '—'}</div>
                      {l.conf_room && <div className="text-surface-muted mt-0.5 font-mono">room: {l.conf_room}</div>}
                      <div className="text-surface-muted mt-1">{fmtTime(l.started_at)} → {fmtTime(l.ended_at)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recordings */}
              <div className="mb-2">
                <div className="text-xs uppercase tracking-wide text-surface-muted mb-1">
                  Recordings — voice {(data.voice_recordings || []).length} · screen {(data.screen_recordings || []).length}
                </div>
                <div className="space-y-1">
                  {(data.voice_recordings || []).map((v, i) => (
                    <div key={`v${i}`} className="border border-surface-border dark:border-surface-dark-border rounded-thin p-2 text-xs flex items-center justify-between">
                      <span>🎙️ voice · {fmtDur(v.duration_sec)} · <span className="text-surface-muted">{v.storage_tier}</span></span>
                      <span className="font-mono text-[10px] break-all">{v.call_id}</span>
                    </div>
                  ))}
                  {(data.screen_recordings || []).map((s, i) => (
                    <div key={`s${i}`} className="border border-surface-border dark:border-surface-dark-border rounded-thin p-2 text-xs flex items-center justify-between">
                      <span>🖥️ screen · {s.status}{s.voice_recording_id ? ' · linked' : ' · unlinked'}</span>
                      <span className="font-mono text-[10px] break-all">{s.call_id}</span>
                    </div>
                  ))}
                  {(data.voice_recordings || []).length === 0 && (data.screen_recordings || []).length === 0 && (
                    <div className="text-surface-muted text-xs">No recordings for this interaction.</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
